import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { supabase } from '../../lib/supabase';
import { LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  getRecoveryTokenFromSearch,
  isImplicitRecoveryHash,
  recoveryVerifyErrorMessage,
  validateRecoveryPassword,
} from '../../utils/authRecovery';

type LinkMode = 'token' | 'session' | 'invalid' | 'checking';

const shellClass =
  'min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4';
const cardClass = 'bg-white rounded-lg shadow-xl p-8 max-w-md w-full';

export function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recoveryVerified, setRecoveryVerified] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [linkMode, setLinkMode] = useState<LinkMode>('checking');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword, verifyRecoveryOtp } = useAuthStore();

  const tokenHash = useMemo(
    () => getRecoveryTokenFromSearch(searchParams.toString()),
    [searchParams]
  );

  useEffect(() => {
    if (tokenHash) {
      setLinkMode('token');
      return undefined;
    }

    const hash = window.location.hash;
    if (hash && isImplicitRecoveryHash(hash)) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
          setLinkMode('session');
          setRecoveryVerified(true);
        }
      });

      void supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          setLinkMode('session');
          setRecoveryVerified(true);
        }
      });

      const timeoutId = setTimeout(() => {
        setLinkMode((prev) => (prev === 'checking' ? 'invalid' : prev));
        setError('Enlace de restablecimiento inválido o expirado');
      }, 2500);

      return () => {
        subscription.unsubscribe();
        clearTimeout(timeoutId);
      };
    }

    setLinkMode('invalid');
    setError('Enlace de restablecimiento inválido o expirado');
    return undefined;
  }, [tokenHash]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError('');
    const validationError = validateRecoveryPassword(newPassword, confirmPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const needsVerify = !recoveryVerified && linkMode === 'token' && tokenHash;

      if (needsVerify) {
        await verifyRecoveryOtp(tokenHash);
        setRecoveryVerified(true);
        window.history.replaceState(null, '', '/reset-password');
      }

      await resetPassword(newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      if (!recoveryVerified) {
        setError(recoveryVerifyErrorMessage(err));
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'No se pudo actualizar la contraseña. Intenta de nuevo.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (linkMode === 'checking') {
    return (
      <div className={shellClass}>
        <div className={`${cardClass} text-center`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Preparando formulario...</p>
        </div>
      </div>
    );
  }

  if (linkMode === 'invalid') {
    return (
      <div className={shellClass}>
        <div className={`${cardClass} text-center`}>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LockClosedIcon className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Enlace Inválido</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Solicitar Nuevo Enlace
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={shellClass}>
        <div className={`${cardClass} text-center`}>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Contraseña Actualizada!</h2>
          <p className="text-gray-600 mb-6">Tu contraseña ha sido restablecida exitosamente.</p>
          <p className="text-sm text-gray-500">Redirigiendo a inicio de sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div className={cardClass}>
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LockClosedIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Nueva Contraseña</h2>
          <p className="text-gray-600">Ingresa tu nueva contraseña</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva Contraseña
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              required
              minLength={6}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Contraseña
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repite tu contraseña"
              required
              minLength={6}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
              {!recoveryVerified && linkMode === 'token' && (
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="mt-2 text-sm font-medium text-red-700 underline"
                >
                  Solicitar nuevo enlace
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !newPassword || !confirmPassword}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Actualizando...' : 'Restablecer Contraseña'}
          </button>
        </form>
      </div>
    </div>
  );
}
